/** Staff: "Start a website order" — payment already taken over the phone / by transfer / cash (or included) → opens a paid order the installer just has to fill in. */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, PhoneCall, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { wget, wpost } from './api';

const METHODS = [
  { key: 'card_phone', label: 'Card over the phone' },
  { key: 'bank_transfer', label: 'Bank transfer' },
  { key: 'cash', label: 'Cash' },
  { key: 'included', label: 'Included in their package' },
];

export function StartOrderDialog({ open, onClose, onStarted }) {
  const [installers, setInstallers] = useState(null);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(null);
  const [method, setMethod] = useState('card_phone');
  const [amount, setAmount] = useState('250');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open && !installers) wget('/staff/installers').then(setInstallers).catch((e) => toast.error(e.message)); }, [open, installers]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (installers || []).filter((i) => !s || [i.name, i.email, i.company_name, i.phone].some((v) => (v || '').toLowerCase().includes(s))).slice(0, 8);
  }, [installers, q]);

  const start = async () => {
    if (!picked) return toast.error('Pick the installer first');
    setBusy(true);
    try {
      const o = await wpost('/staff/website/orders/start', { user_id: picked.user_id, method, amount_paid: method === 'included' ? 0 : Number(amount || 0), note, notify });
      toast.success(`Website order started for ${picked.name}${notify ? ' — they’ve been told to fill in their details' : ''}`);
      onStarted(o);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="start-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PhoneCall className="w-5 h-5 text-teal-700" /> Start a website order</DialogTitle>
          <DialogDescription>Use this when an installer has paid over the phone, by transfer or in cash. We open a paid order and they just fill in their business details in the app.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Installer</Label>
            {picked ? (
              <div className="flex items-center justify-between rounded-lg border bg-teal-50 border-teal-200 px-3 py-2" data-testid="start-order-picked">
                <div><div className="text-sm font-semibold">{picked.name}</div><div className="text-xs text-gray-600">{picked.company_name ? `${picked.company_name} · ` : ''}{picked.email}{picked.phone ? ` · ${picked.phone}` : ''}</div></div>
                <Button size="sm" variant="ghost" onClick={() => setPicked(null)} data-testid="start-order-change">Change</Button>
              </div>
            ) : (
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, company, email or mobile…" className="pl-9" autoFocus data-testid="start-order-search" />
                {q && <ul className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto divide-y">
                  {!installers && <li className="p-3 text-xs text-gray-400"><Loader2 className="w-4 h-4 animate-spin inline" /></li>}
                  {installers && !list.length && <li className="p-3 text-xs text-gray-400">No installer matches</li>}
                  {list.map((i) => <li key={i.user_id}><button type="button" onClick={() => { setPicked(i); setQ(''); }} className="w-full text-left px-3 py-2 hover:bg-teal-50" data-testid={`start-order-pick-${i.user_id}`}><div className="text-sm font-semibold">{i.name}</div><div className="text-xs text-gray-500">{i.company_name ? `${i.company_name} · ` : ''}{i.email}</div></button></li>)}
                </ul>}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">How did they pay?</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="start-order-methods">
              {METHODS.map((m) => <button key={m.key} type="button" onClick={() => setMethod(m.key)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${method === m.key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}`} data-testid={`start-order-method-${m.key}`}>{m.label}</button>)}
            </div>
          </div>
          {method !== 'included' && (
            <div>
              <Label className="text-xs">Amount taken (£)</Label>
              <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="max-w-[160px]" data-testid="start-order-amount" />
              <p className="text-[11px] text-gray-500 mt-1">Web + CRM is £200 setup + £50 first month = £250. {method === 'cash' && 'Cash is added to the Cash Log automatically.'}</p>
            </div>
          )}
          <div><Label className="text-xs">Note (optional)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={300} placeholder="e.g. paid on 20 Oct, receipt emailed" data-testid="start-order-note" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="start-order-notify" /> Text &amp; email them now to fill in their details</label>
          <Button onClick={start} disabled={busy || !picked} className="w-full h-11 bg-teal-700 hover:bg-teal-800 text-white font-bold" data-testid="start-order-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PhoneCall className="w-4 h-4 mr-2" />} Start paid order</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
